import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { ThemeContext } from "styled-components";
import { Avatar } from "../avatar/avatar.es.js";
import { FileInput } from "./file-input.es.js";
import { Icon } from "../icon/icon.es.js";
import "../anchor/anchor.es.js";
import { Tooltip } from "../tooltip/tooltip.es.js";
import { AvatarUploaderWrapper, AvatarWithOverlay, Overlay, AvatarUploaderText, HiddenFileInputWrapper, AvatarDropzone } from "./avatar-uploader.styles.es.js";
import { useEventListener } from "../../hooks/use-event-listener.es.js";
import { AnchorImpl } from "../anchor/anchor.styles.es.js";
const propTypes = {
  image: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  toast: PropTypes.func,
  variant: PropTypes.oneOf(["individual", "organisation"])
};
const defaultProps = {
  image: void 0,
  name: void 0,
  toast: void 0,
  variant: "organisation"
};
const AvatarUploader = ({ image, name, onChange, toast, variant }) => {
  const dropzoneRef = useRef();
  const fileInputRef = useRef();
  const { color } = useContext(ThemeContext);
  const [showDropzone, setShowDropzone] = useState(false);
  const validFileTypes = ["image/png", "image/jpg", "image/jpeg", "image/gif"];
  const openFileInputDialog = () => {
    if (!fileInputRef.current)
      return;
    fileInputRef.current.click();
  };
  useEffect(() => {
    const timerId = setTimeout(() => {
      if (!showDropzone)
        dropzoneRef.current.style.display = "none";
    }, 200);
    return () => {
      clearTimeout(timerId);
    };
  }, [showDropzone]);
  useEventListener("dragenter", () => {
    setShowDropzone(true);
    dropzoneRef.current.style.display = "block";
  });
  useEventListener("dragover", (e) => {
    setShowDropzone(true);
    e.preventDefault();
  });
  useEventListener("dragleave", () => {
    setShowDropzone(false);
  });
  useEventListener("drop", async (e) => {
    e.preventDefault();
    setShowDropzone(false);
    dropzoneRef.current.style.display = "none";
    const { files } = e.dataTransfer;
    if (files.length > 1) {
      if (toast) {
        toast("Please drop only one image.", {
          clickable: true,
          duration: 6e3,
          variant: "info"
        });
      }
      return;
    }
    const invalidFileType = !validFileTypes.includes(files[0].type);
    const imageTooLarge = files[0].size > 2e6;
    if (invalidFileType || imageTooLarge) {
      if (toast) {
        toast("Only PNG, JPG, and GIF files smaller than 2 MB are supported.", {
          clickable: true,
          duration: 6e3,
          variant: "error"
        });
      }
    } else {
      fileInputRef.current.files = files;
      const results = await Promise.all(
        Array.from(files).map(
          (file) => new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              resolve(reader.result);
            };
          })
        )
      );
      onChange(results[0]);
    }
  });
  return /* @__PURE__ */ jsxs(AvatarUploaderWrapper, { "data-testid": "avatar-uploader", children: [
    /* @__PURE__ */ jsxs(AvatarWithOverlay, { children: [
      /* @__PURE__ */ jsx(
        Avatar,
        {
          image,
          magnitude: "xlarge",
          onClick: openFileInputDialog,
          variant,
          children: !image && (name || /* @__PURE__ */ jsx(Icon, { type: variant === "organisation" ? "building" : "user" }))
        }
      ),
      image ? /* @__PURE__ */ jsx(Tooltip, { placement: "top", title: "Remove image", children: /* @__PURE__ */ jsx(Overlay, { variant, onClick: () => onChange(void 0), children: /* @__PURE__ */ jsx(Icon, { color: color.red6, magnitude: "medium", type: "delete" }) }) }) : /* @__PURE__ */ jsx(Overlay, { variant, onClick: openFileInputDialog, children: /* @__PURE__ */ jsx(Icon, { magnitude: "medium", type: "upload" }) })
    ] }),
    /* @__PURE__ */ jsxs(AvatarUploaderText, { children: [
      "Drag and drop an image",
      /* @__PURE__ */ jsx("br", {}),
      /* @__PURE__ */ jsxs("span", { children: [
        "or",
        " ",
        /* @__PURE__ */ jsx(AnchorImpl, { as: "button", onClick: openFileInputDialog, children: "browse" }),
        " ",
        "to choose a file"
      ] })
    ] }),
    /* @__PURE__ */ jsx(HiddenFileInputWrapper, { children: /* @__PURE__ */ jsx(
      FileInput,
      {
        accept: validFileTypes,
        onChange,
        ref: fileInputRef
      }
    ) }),
    /* @__PURE__ */ jsx(AvatarDropzone, { id: "dropzone", ref: dropzoneRef, children: "Drop file to upload." })
  ] });
};
AvatarUploader.propTypes = propTypes;
AvatarUploader.defaultProps = defaultProps;
export {
  AvatarUploader
};
